import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { resolvePov } from '@/lib/battlePov';
import { pickKeyCards } from '@/lib/winConditions';
import { modeBucket, modeLabelKey } from '@/lib/battleMode';
import type { CRBattle } from '@/types/qbot';
import { CardStrip } from './CardStrip';

interface BattleRowProps {
  battle: CRBattle;
  /** Tag of the player whose POV we render (with or without '#'). */
  playerTag: string;
  onClick?: () => void;
}

/** CR battleTime "20260520T214500.000Z" → short relative label. */
function formatRelativeTime(iso: string): string {
  const m = iso.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return '';
  const date = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`);
  const mins = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const RAIL: Record<'win' | 'loss' | 'draw', string> = {
  win: 'bg-emerald-400',
  loss: 'bg-rose-400',
  draw: 'bg-amber-400',
};

export function BattleRow({ battle, playerTag, onClick }: BattleRowProps) {
  const { t } = useTranslation('cr');
  const pov = resolvePov(battle, playerTag);
  if (!pov) return null;
  const { me, opp, result } = pov;

  const keyCards = pickKeyCards(opp.cards, 4);
  const modeLabel = t(modeLabelKey(modeBucket(battle)), { defaultValue: battle.gameMode?.name ?? battle.type });
  const delta = me.trophyChange;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'card-edge flex w-full items-center gap-2.5 rounded-xl bg-bg-secondary p-2.5 text-left transition-opacity active:opacity-80',
        onClick ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <span className={cn('h-11 w-[3px] shrink-0 rounded', RAIL[result])} aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{opp.name}</span>
          <span className="shrink-0 text-[11px] text-fg-muted">
            {modeLabel} · {formatRelativeTime(battle.battleTime)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <CardStrip cards={keyCards} />
          <div className="flex shrink-0 items-center gap-2">
            <span className="tabular-nums text-sm font-bold text-fg">
              {me.crowns}<span className="text-fg-muted">–</span>{opp.crowns}
            </span>
            {typeof delta === 'number' && delta !== 0 && (
              <span className={cn('tabular-nums text-[11px] font-bold', delta > 0 ? 'text-emerald-300' : 'text-rose-300')}>
                {delta > 0 ? '+' : ''}{delta}🏆
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
